import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { Alert, AlertDescription } from "@/components/ui/alert";

export default function ChangePasswordPage() {
  const { user, changePassword, logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error("Password must be at least 8 characters");
    if (next !== confirm) return toast.error("Passwords do not match");
    setSaving(true);
    try {
      await changePassword(current, next);
      toast.success("Password updated");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen auth-bg grid place-items-center p-6">
      <Card className="w-full max-w-lg border-border shadow-none">
        <CardContent className="p-8">
          <div className="tiny-label">Account security</div>
          <h2 className="font-display text-2xl font-bold mt-2">Change your password</h2>
          <p className="text-sm text-muted-foreground mt-1">Use at least 8 characters, including a number and a symbol.</p>

          {user?.must_change_password && (
            <Alert className="mt-5 border-amber-200 bg-amber-50 text-amber-800">
              <AlertDescription>Your account uses a temporary password. You must set a new one before continuing.</AlertDescription>
            </Alert>
          )}

          <form className="mt-6 space-y-4" onSubmit={onSubmit}>
            <div>
              <Label htmlFor="current">Current password</Label>
              <Input id="current" type="password" required className="mt-1.5 h-11" value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="cp-current" />
            </div>
            <div>
              <Label htmlFor="new">New password</Label>
              <Input id="new" type="password" required className="mt-1.5 h-11" value={next} onChange={(e) => setNext(e.target.value)} data-testid="cp-new" />
            </div>
            <div>
              <Label htmlFor="confirm">Confirm new password</Label>
              <Input id="confirm" type="password" required className="mt-1.5 h-11" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="cp-confirm" />
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={saving} className="flex-1 h-11" data-testid="cp-submit">{saving ? "Saving…" : "Update password"}</Button>
              <Button type="button" variant="outline" className="h-11" onClick={async () => { await logout(); navigate("/login"); }} data-testid="cp-logout">Sign out</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
